import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import i18n from '@nextroll/ar-i18n';
import { Subnav } from '@nextroll/ar-app';
import { useContainerContext } from '@nextroll/ar-container-context';
import SlidingModal from '@nextroll/ar-sliding-modal';
import { urlService } from '@nextroll/ar-container-app';

import { IWorkflowTemplate } from '../../../types/templates';
import { IWorkflow, IWorkflowValidationResult, WORKFLOW_BASE } from '../../../types/rofl';
import { getWorkflowList, getWorkflow, validateWorkflow, getWorkflowTemplates } from '../../../api/workflowsApi';
import { getWorkflowsFeature } from '../../../util/featureFlags';
import { getAppPath, getWorkflowSetupRoute, getWorkflowByEidRoute } from '../../../util/urls';
import { WorkflowPathType } from '../../../util/util';
import { WORKFLOW_DEFAULT_MAX_LIMIT, WORKFLOW_CLONE_TEMP_EID, WORKFLOW_DEFAULT_NAMES } from '../../../util/workflow.constants';
import useRequest from '../../../hooks/useRequest';

import { CanvasPage } from '../../pages/CanvasPage/CanvasPage';
import { WorkflowTemplates } from '../..//basic/WorkflowTemplates/WorkflowTemplates';
import { WorkflowListTable } from '../../basic/WorkflowListTable/WorkflowListTable';
import { MaxOnWorkflowsLimitReachedTooltip } from '../../basic/WorkflowListTable/WorkflowListTooltips/WorkflowListTooltips';

interface IProps {
    workflow?: Partial<IWorkflow>;
    setWorkflow: (workflow: Partial<IWorkflow> | {} | undefined) => void;
}

export const WorkflowListPage = (props: IProps) => {
    const { workflow, setWorkflow } = props;

    const { pathId } = useParams<{ pathId: string }>();
    const location = useLocation();
    const { advertisable, setPageTitle, navigateTo } = useContainerContext();
    const advertisableEid = advertisable?.eid;
    const [callWorkflows, loadingWorkflows] = useRequest();
    const [callWorkflow, loadingWorkflow] = useRequest();

    // max number of workflows that allowed to create; if the value is missing in the feature, then set to 25
    const maxOnWorkflowsLimit = getWorkflowsFeature().quantity || WORKFLOW_DEFAULT_MAX_LIMIT;

    const [data, setData] = useState<IWorkflow[]>([]);
    const [showFirstCreateModal, setShowFirstCreateModal] = useState(false);

    const [templates, setTemplates] = useState(undefined as IWorkflowTemplate[] | undefined);

    const templateEid = useMemo(
        () => {
            const query = new URLSearchParams(location.search);
            return query.get('template') || undefined;
        }, [location.search]
    );

    /**
     * determine if the workflow table is empty; if so, then show the splash instead
     */
    const noExistingWorkflows = !loadingWorkflows && data.length === 0;

    /**
     * determine if the user can turn on a workflow
     */
    const canTurnOnWorkflow = !loadingWorkflows && (data.filter(w => w.status === 'on').length < maxOnWorkflowsLimit);

    /**
     * fetch the workflow list as the list view table content when:
     * - as soon as the page is mount, or;
     * - when the canvas view is dismissed and returning to the list view
     */
    const loadWorkflows = useRef(async () => {
        if (!advertisableEid) {
            return;
        }

        const response = await callWorkflows(async () => getWorkflowList(advertisableEid));
        response && Array.isArray(response) && setData(response);
    });

    /**
     * fetch workflow templates
     */
    const loadWorkflowTemplates = useRef(async () => {
        if (!advertisableEid) {
            return;
        }
        const response = await getWorkflowTemplates(advertisableEid, 'adroll');
        response && Array.isArray(response) && setTemplates(response);
    });

    /**
     * fetch a select workflow content
     */
    const loadAndValidateWorkflow = useRef(async () => {
        if (!advertisableEid || !pathId) {
            return;
        }

        // fetch both the given workflow and the validations
        // note we want to handle corresponding errors separately
        const [workflowFromRequest, workflowValidationResult] = await Promise.all([
            callWorkflow(() => getWorkflow(pathId, advertisableEid)),
            validateWorkflow(pathId, advertisableEid),
        ].map(promise => promise.catch(e => e))) as [IWorkflow, IWorkflowValidationResult];

        // redirect to the "Not Found" page only when the GET workflow request throws an error
        if (workflowFromRequest instanceof Error) {
            navigateTo({ to: `${getAppPath()}/${pathId}/notfound` });
            return;
        }

        setWorkflow({
            ...workflowFromRequest,
            errors: {
                schema_errors: {
                    trigger_errors: [
                        ...(workflowFromRequest.errors?.schema_errors.trigger_errors || []),
                        ...(workflowValidationResult?.schema_errors?.trigger_errors || []),
                    ],
                    action_errors: [
                        ...(workflowFromRequest.errors?.schema_errors.action_errors || []),
                        ...(workflowValidationResult?.schema_errors?.action_errors || []),
                    ],
                },
            },
            warnings: {
                schema_warnings: {
                    trigger_warnings: [
                        ...(workflowFromRequest.warnings?.schema_warnings.trigger_warnings || []),
                        ...(workflowValidationResult?.schema_warnings?.trigger_warnings || []),
                    ],
                    action_warnings: [
                        ...(workflowFromRequest.warnings?.schema_warnings.action_warnings || []),
                        ...(workflowValidationResult?.schema_warnings?.action_warnings || []),
                    ],
                },
            },
        } as Partial<IWorkflow>);
    });

    /**
     * load page content according to the path ID and workflow data
     */
    useEffect(() => {
        // workflow list page
        if (!pathId) {
            loadWorkflows.current();
            loadWorkflowTemplates.current();
            workflow && setWorkflow(undefined); // cleanup the workflow object if path ID is lost
            return;
        }
        // edit an existing workflow
        if (pathId !== WorkflowPathType.SETUP && !workflow?.eid) {
            loadAndValidateWorkflow.current();
            return;
        }
        // parachute from a template URL
        if (pathId === WorkflowPathType.SETUP && templateEid) {
            loadWorkflowTemplates.current();
        }
    }, [pathId, templateEid, workflow, setWorkflow]);

    /**
     * Handle the /setup route separately in order to manage templates properly and avoid repeating
     * requests once templates are loaded.
     */
    useEffect(() => {
        // create new workflow
        if (pathId === WorkflowPathType.SETUP) {
            if (!templateEid) {
                !workflow && setWorkflow({});
                return;
            }
            if (!templates) {
                return;
            }
            const template = templates.find(t => t.eid === templateEid);
            if (!template) {
                !workflow && setWorkflow({});
                return;
            }
            !workflow?.schema && setWorkflow({
                name: WORKFLOW_DEFAULT_NAMES[template.eid] || template.name,
                schema: template.schema,
                template_eid: template.eid,
                entity_type: template.entity_type || WORKFLOW_BASE.CONTACTS,
            });
        }
    }, [pathId, setWorkflow, templateEid, templates, workflow]);

    /**
     * set page titles
     */
    useEffect(() => {
        const subtitle = pathId === WorkflowPathType.SETUP
            ? i18n.gettext('Create a Workflow')
            : (workflow?.name || i18n.gettext('Workflows'));
        setPageTitle?.({
            title: i18n.gettext('AdRoll'),
            subtitle,
        });
    }, [pathId, setPageTitle, workflow?.name]);

    /**
     * event handler
     */
    const handleSelectRow = (row: IWorkflow) => {
        setWorkflow(row);
        const to = row.eid && row.eid !== WORKFLOW_CLONE_TEMP_EID
            ? getWorkflowByEidRoute(row.eid, location.search)
            : getWorkflowSetupRoute();
        navigateTo({ to });
    };

    const btnCreate = !noExistingWorkflows && (
        <button
            type='button'
            role='button'
            disabled={loadingWorkflows || !canTurnOnWorkflow}
            className='btn btn-primary'
            onClick={() => setShowFirstCreateModal(true)}
        >
            {i18n.gettext('Create Workflow')}
        </button>
    );

    /**
     * wrap the create button with the tooltip if:
     * - if the total number of workflows exceeds the limit, and;
     * - if the list table is not in loading
     */
    const btnCreateMaybeWithTooltip = (canTurnOnWorkflow || loadingWorkflows) ? btnCreate : !!btnCreate && (
        <MaxOnWorkflowsLimitReachedTooltip
            canTurnOnWorkflow={canTurnOnWorkflow}
            maxOnWorkflowsLimit={maxOnWorkflowsLimit}
        >
            {btnCreate}
        </MaxOnWorkflowsLimitReachedTooltip>
    );

    const listViewContent = noExistingWorkflows
        ? (
            <WorkflowTemplates
                templates={templates}
            />
        )
        : (
            <WorkflowListTable
                loading={loadingWorkflows}
                data={data}
                onUpdate={workflows => setData(workflows || [])}
                onSelect={handleSelectRow}
                canTurnOnWorkflow={canTurnOnWorkflow}
                maxOnWorkflowsLimit={maxOnWorkflowsLimit}
            />
        );

    const canvasViewContent = workflow && (
        <CanvasPage
            workflow={workflow}
            setWorkflow={setWorkflow}
            loadingWorkflow={loadingWorkflow}
            // It's necessary to retain the advertisable in the querystring here to remove all query parameters
            // when closing the canvas page. navigateTo will do a hard refresh if there's no query and no sub-path.
            onClose={() => navigateTo({ to: urlService.getURLWithAdvertisable(getAppPath(), advertisable) as string })}
        />
    );

    return (
        <>
            <Subnav
                i18n={i18n}
                title={i18n.gettext('Workflows')}
                actionItem={btnCreateMaybeWithTooltip}
            />
            <div className='workflows-workflow-list-page'>{listViewContent}</div>
            <div className={`workflows-canvas-page${pathId ? ' show' : ''}`}>{canvasViewContent}</div>

            {!noExistingWorkflows && (
                <SlidingModal
                    show={showFirstCreateModal}
                    title={i18n.gettext('Workflows')}
                    size={SlidingModal.SLIDING_MODAL_LARGE}
                    onClose={() => setShowFirstCreateModal(false)}
                    className='workflows-first-create-splash'
                >
                    <WorkflowTemplates
                        onCreate={() => setShowFirstCreateModal(false)}
                        templates={templates}
                    />
                </SlidingModal>
            )}
        </>
    );
};
